import { Box, Popover, Tab, Tabs } from "@mui/material";
import { Button, Typography } from "@platform/design-system";
import { type SyntheticEvent, useState } from "react";

import TabIndicator from "./TabIndicator";

import type { CarrierNoteState, CarrierTab } from "./types";

interface CarrierNotesTabBarProps {
  carriers: CarrierTab[];
  activeCarrierId: string;
  getCarrierState: (carrierId: string) => CarrierNoteState;
  onTabChange: (carrierId: string) => void;
  onRetry: (carrierId: string) => void;
}

const CarrierNotesTabBar = ({
  carriers,
  activeCarrierId,
  getCarrierState,
  onTabChange,
  onRetry,
}: CarrierNotesTabBarProps): React.JSX.Element => {
  const [errorAnchor, setErrorAnchor] = useState<{
    element: HTMLElement;
    carrierId: string;
  } | null>(null);

  const handleTabChange = (_: SyntheticEvent, value: string): void => {
    onTabChange(value);
  };

  const handleErrorClick = (
    event: React.MouseEvent<HTMLElement>,
    carrierId: string,
  ): void => {
    event.stopPropagation();
    setErrorAnchor({ element: event.currentTarget, carrierId });
  };

  const handleRetry = (): void => {
    if (errorAnchor) {
      onRetry(errorAnchor.carrierId);
      setErrorAnchor(null);
    }
  };

  return (
    <>
      <Tabs
        value={activeCarrierId}
        onChange={handleTabChange}
        variant="scrollable"
        scrollButtons="auto"
        aria-label="Carrier notes tabs"
        textColor="secondary"
        indicatorColor="secondary"
        sx={{
          minHeight: 40,
          "& .MuiTabs-indicator": {
            height: 2,
            backgroundColor: "brandPrimary.main",
          },
          "& .MuiTab-root.Mui-selected": { color: "brandPrimary.main" },
          "& .MuiTab-root": {
            minHeight: 40,
            py: 1,
            textTransform: "uppercase",
            fontWeight: 600,
            fontSize: "0.75rem",
            letterSpacing: "0.08em",
          },
        }}
      >
        {carriers.map((carrier) => {
          const state = getCarrierState(carrier.carrierId);
          return (
            <Tab
              key={carrier.carrierId}
              value={carrier.carrierId}
              label={
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  {carrier.carrierName}
                  <Box
                    onClick={
                      state.saveState === "error"
                        ? (e: React.MouseEvent<HTMLElement>) =>
                            handleErrorClick(e, carrier.carrierId)
                        : undefined
                    }
                  >
                    <TabIndicator
                      saveState={state.saveState}
                      hasContent={state.hasContent}
                    />
                  </Box>
                </Box>
              }
            />
          );
        })}
      </Tabs>

      <Popover
        open={!!errorAnchor}
        anchorEl={errorAnchor?.element}
        onClose={() => setErrorAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <Box sx={{ p: 2, maxWidth: 250 }}>
          <Typography variant="body-medium" sx={{ mb: 1 }}>
            Failed to save… retry?
          </Typography>
          <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
            <Button
              variant="tertiary"
              size="sm"
              onClick={() => setErrorAnchor(null)}
            >
              Cancel
            </Button>
            <Button variant="tertiary" size="sm" onClick={handleRetry}>
              Retry
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
};

export default CarrierNotesTabBar;
